import { EmailIntegration } from 'lib-shared-legacy-types';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { Satisfaction } from 'lib-shared-legacy-types';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { languages } from 'app/utils/supportedLanguages';
import { EmailCSAT } from 'components/settings/components/sections/satisfaction/components/EmailCSAT/EmailPage/types';
import { SatisfactionState } from 'components/settings/reducers/sections/satisfaction';
import { withRouter } from 'hocs/router';

import satisfactionActions from '../../../actions/satisfaction';
import {
  DispatchProps,
  EmailAccountSetting,
  EmailPanel,
  StateProps,
} from '../../../components/sections/satisfaction/components/EmailCSAT/EmailPanel/EmailPanel';
import { emailListAll as createLoadAllEmailsAction } from '../../../reducers/sections/email';

type State = {
  settings: {
    satisfaction: SatisfactionState;
    email: {
      emailList: EmailIntegration[];
      meta: { asyncStatus: AsyncStatus };
    };
  };
};

const mapStateToProps = (state: State): StateProps => ({
  globalSettings: {
    values: state.settings.satisfaction.customization.email.globalLevel.values,
    asyncStatus:
      state.settings.satisfaction.customization.email.globalLevel.asyncStatus,
  },

  emailAccountsSettings: {
    asyncStatus: state.settings.email.meta.asyncStatus,
    list: state.settings.email.emailList.map(
      (emailIntegration): EmailAccountSetting => {
        const correspondingSettings =
          state.settings.satisfaction.customization.email.contactPointsLevel.values?.find(
            ({ contactPoint }) =>
              contactPoint === emailIntegration.email_address,
          );

        if (correspondingSettings) {
          return {
            address: emailIntegration.email_address,
            enabled: correspondingSettings.enabled,
            settings: correspondingSettings,
          };
        }

        return {
          address: emailIntegration.email_address,
          enabled: false,
          settings: null,
        };
      },
    ),
  },

  languagesSettings: {
    list: languages.map(({ code, label }) => {
      const correspondingSettings =
        state.settings.satisfaction.customization.email.languagesLevel.values?.find(
          ({ language }) => language === code,
        );

      if (correspondingSettings) {
        return { code, label, settings: correspondingSettings };
      }

      return { code, label, settings: null };
    }),
    asyncStatus:
      state.settings.satisfaction.customization.email.languagesLevel
        .asyncStatus,
  },
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    loadAllEmails() {
      dispatch(createLoadAllEmailsAction());
    },

    loadEmailSettingsAllLevels() {
      dispatch(
        satisfactionActions.satisfactionLoadEmailAllLevelsSettingsRequest(),
      );
    },

    initializeEmailCsat() {
      dispatch(satisfactionActions.satisfactionInitializeRequest('email'));
    },

    createGlobalSettings: (
      settings: Satisfaction.CreateEmailCsatSettingsParams,
    ) => {
      const action =
        satisfactionActions.satisfactionSaveEmailGlobalSettingsRequest(
          settings,
          { edit: false },
        );
      dispatch(action);
    },

    updateGlobalSettings: (
      settings: Satisfaction.UpdateEmailCsatSettingsParams,
    ) => {
      const action =
        satisfactionActions.satisfactionSaveEmailGlobalSettingsRequest(
          settings,
          { edit: true },
        );
      dispatch(action);
    },

    updateEmailLevelSettings: ({
      settings,
      ...change
    }: EmailCSAT.EmailAccountStatusChange) => {
      const action =
        satisfactionActions.satisfactionSaveEmailContactLevelSettingsRequest(
          change,
          settings,
        );
      dispatch(action);
    },

    updateLanguageLevelSettings(
      payload: Partial<Satisfaction.EmailCsatSettings>,
    ) {
      const action =
        satisfactionActions.satisfactionSaveEmailLanguageLevelSettingsRequest(
          payload,
        );

      dispatch(action);
    },
  };
};

export default withRouter(
  connect<StateProps, DispatchProps, Record<string, unknown>, State>(
    mapStateToProps,
    mapDispatchToProps,
  )(EmailPanel),
);
